import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link, useNavigate } from 'react-router-dom';
import PageBasedPagination from '@/components/ui/PageBasedPagination';
import FullscreenError from '@/components/utils/FullscreenError';
import CreateStockExchangeModal from '@/components/admin/stock-exchanges/CreateStockExchangeModal';
import EditStockExchangeModal from '@/components/admin/stock-exchanges/EditStockExchangeModal';
import { faGlobe, faSpinner, faArrowLeft, faPlus, faEdit, faTrash, faCircleInfo } from '@/fontawesome';
import { useUser } from '@/contexts/UserContext';
import { handleAPIError, showSuccessToast, showErrorToast } from '@/utils/toasts';
import { formatDate, secondsToTimeString } from '@/utils/helpers';
import { APIRoutes } from '@/services/api.routes';
import type { StockExchange, StockExchangeID } from '@/models';

const pageSize = 50;

export default function AdminStockExchangesList() {
  const { isAdmin } = useUser();
  const navigate = useNavigate();
  const [stockExchanges, setStockExchanges] = useState<StockExchange[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingStockExchange, setEditingStockExchange] = useState<StockExchange | null>(null);
  const [criticalError, setCriticalError] = useState<boolean>(false);
  const [deletingStockExchange, setDeletingStockExchange] = useState<StockExchangeID | null>(null);

  useEffect(() => {
    fetchStockExchanges();
  }, [currentPage]);

  const fetchStockExchanges = async () => {
    try {
      setLoading(true);
      setError(null);
      setCriticalError(false);

      // Generate cursor token for page-based navigation
      const cursorToken = currentPage > 1 ? `$${currentPage}` : undefined;

      const response = await APIRoutes.listStockExchanges({
        limit: pageSize,
        next: cursorToken
      });
      setStockExchanges(response.stockExchanges);
      setTotal(response.total);
    } catch (err) {
      console.error('Critical error loading stock exchanges list:', err);
      setError('Backend nicht erreichbar oder Fehler beim Laden der Börsenliste');
      setCriticalError(true);
      handleAPIError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleStockExchangeCreated = (newStockExchange: StockExchange) => {
    setStockExchanges(prev => [newStockExchange, ...prev]);
    setTotal(prev => prev + 1);
    showSuccessToast('Börse erfolgreich erstellt');
  };

  const handleEditStockExchange = (stockExchange: StockExchange) => {
    setEditingStockExchange(stockExchange);
    setIsEditModalOpen(true);
  };

  const handleStockExchangeUpdated = (updatedStockExchange: StockExchange) => {
    setStockExchanges(prevStockExchanges =>
      prevStockExchanges.map(stockExchange =>
        stockExchange.id === updatedStockExchange.id ? updatedStockExchange : stockExchange
      )
    );
    showSuccessToast('Börse erfolgreich aktualisiert');
  };

  const handleDeleteStockExchange = async (stockExchangeId: StockExchangeID) => {
    if (!confirm('Sind Sie sicher, dass Sie diese Börse löschen möchten?')) {
      return;
    }

    try {
      setDeletingStockExchange(stockExchangeId);
      await APIRoutes.deleteStockExchange(stockExchangeId);
      setStockExchanges(prev => prev.filter(stockExchange => stockExchange.id !== stockExchangeId));
      setTotal(prev => prev - 1);
      showSuccessToast('Börse erfolgreich gelöscht');
    } catch (err) {
      console.error('Error deleting stock exchange:', err);
      showErrorToast('Fehler beim Löschen der Börse');
      handleAPIError(err);
    } finally {
      setDeletingStockExchange(null);
    }
  };

  // Show fullscreen error if critical error occurred
  if (criticalError) {
    return (
      <FullscreenError
        title="Börsenliste nicht verfügbar"
        message={error ?? 'Die Börsenliste konnte nicht geladen werden. Möglicherweise ist das Backend nicht erreichbar.'}
        onRetry={fetchStockExchanges}
        onBack={() => navigate('/admin')}
      />
    );
  }

  return (
    <>
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center">
          <Link
            to="/admin"
            className="mr-4 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="w-5 h-5" />
          </Link>
          <div className="flex items-center">
            <FontAwesomeIcon icon={faGlobe} className="w-8 h-8 text-cyan-600 dark:text-cyan-400 mr-3" />
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Börsen ({total})
            </h1>
          </div>
        </div>
        <button
          onClick={() => setIsCreateModalOpen(true)}
          disabled={!isAdmin}
          title={isAdmin ? 'Neue Börse erstellen' : 'Nur für Administratoren'}
          className={`px-4 py-2 bg-cyan-600 text-white rounded-lg transition-colors flex items-center ${!isAdmin ? 'opacity-50 cursor-not-allowed' : 'hover:bg-cyan-700 cursor-pointer'}`}
        >
          <FontAwesomeIcon icon={faPlus} className="w-4 h-4 mr-2" />
          Neue Börse
        </button>
      </div>

      {/* Info Banner */}
      <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 flex items-center">
        <FontAwesomeIcon icon={faCircleInfo} className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-3 flex-shrink-0" />
        <p className="text-sm text-blue-800 dark:text-blue-200">
          Quelle Identifier (für googleSheets):{' '}
          <a
            href="https://www.google.com/googlefinance/disclaimer/"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-blue-600 dark:hover:text-blue-300"
          >
            https://www.google.com/googlefinance/disclaimer/
          </a>
        </p>
      </div>

      {loading && (
        <div className="flex justify-center items-center py-12">
          <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-cyan-600 dark:text-cyan-400 animate-spin" />
        </div>
      )}

      {error !== null && !criticalError && (
        <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg mb-6">
          <p className="text-red-600 dark:text-red-400">{error}</p>
          <button
            onClick={fetchStockExchanges}
            className="mt-2 text-red-600 dark:text-red-400 underline hover:text-red-800 dark:hover:text-red-300"
          >
            Erneut versuchen
          </button>
        </div>
      )}

      {!loading && error === null && stockExchanges.length === 0 && (
        <div className="text-center py-12">
          <FontAwesomeIcon icon={faGlobe} className="w-16 h-16 text-gray-400 dark:text-gray-600 mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Keine Börsen gefunden
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Erstellen Sie Ihre erste Börse.
          </p>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            disabled={!isAdmin}
            title={isAdmin ? 'Erste Börse erstellen' : 'Nur für Administratoren'}
            className={`px-6 py-3 bg-cyan-600 text-white rounded-lg transition-colors ${!isAdmin ? 'opacity-50 cursor-not-allowed' : 'hover:bg-cyan-700 cursor-pointer'}`}
          >
            <FontAwesomeIcon icon={faPlus} className="w-4 h-4 mr-2" />
            Erste Börse erstellen
          </button>
        </div>
      )}

      {!loading && stockExchanges.length > 0 && (
        <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Identifier
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Zeitzone
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Handelszeiten
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Datenquellen-Codes
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Erstellt
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Aktualisiert
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Aktionen
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {stockExchanges.map((stockExchange) => (
                  <tr
                    key={stockExchange.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {stockExchange.identifier}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {stockExchange.name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {stockExchange.timezone}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {secondsToTimeString(stockExchange.openingTime)} - {secondsToTimeString(stockExchange.closingTime)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">
                        <span title="Google-Sheets-Code (leer = Identifier)">
                          <span className="text-gray-500 dark:text-gray-400">GS:</span>{' '}
                          <span className="font-mono">{stockExchange.googleSheetsCode ?? '—'}</span>
                        </span>
                        <span className="text-gray-400 dark:text-gray-500"> · </span>
                        <span title="EODHD-Börsencode (leer = keine Standard-Referenz)">
                          <span className="text-gray-500 dark:text-gray-400">EODHD:</span>{' '}
                          <span className="font-mono">{stockExchange.eodhdCode ?? '—'}</span>
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(stockExchange.created)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {stockExchange.updated !== null ? formatDate(stockExchange.updated) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => handleEditStockExchange(stockExchange)}
                          disabled={!isAdmin}
                          className={`text-blue-600 dark:text-blue-400 ${!isAdmin ? 'opacity-50 cursor-not-allowed' : 'hover:text-blue-900 dark:hover:text-blue-300 cursor-pointer'}`}
                          title={isAdmin ? 'Bearbeiten' : 'Nur für Administratoren'}
                        >
                          <FontAwesomeIcon icon={faEdit} className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteStockExchange(stockExchange.id)}
                          disabled={!isAdmin || deletingStockExchange === stockExchange.id}
                          className={`text-red-600 dark:text-red-400 disabled:opacity-50 ${!isAdmin ? 'cursor-not-allowed' : 'hover:text-red-900 dark:hover:text-red-300 cursor-pointer'}`}
                          title={isAdmin ? 'Löschen' : 'Nur für Administratoren'}
                        >
                          {deletingStockExchange === stockExchange.id ? (
                            <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                          ) : (
                            <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <PageBasedPagination
            currentPage={currentPage}
            totalCount={total}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            loading={loading}
          />
        </div>
      )}

      {/* Create Stock Exchange Modal */}
      <CreateStockExchangeModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onStockExchangeCreated={handleStockExchangeCreated}
      />

      {/* Edit Stock Exchange Modal */}
      {editingStockExchange !== null && (
        <EditStockExchangeModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setEditingStockExchange(null);
          }}
          stockExchange={editingStockExchange}
          onStockExchangeUpdated={handleStockExchangeUpdated}
        />
      )}
    </>
  );
}
